import styled from '@emotion/styled';
import React, { useMemo } from 'react';
import { animated, useSpring } from 'react-spring';
import { useHighlightedTrackers, useIsAdmin, useIsReseller } from '../../../lib/customHooks';
import { useStore } from '../../../store/reducers';
import { IconArrowDoubleRight } from '../../Icon';
import { ActionsDropDown } from '../ActionsDropDown';
import { ConnectScannerButton } from '../ActionsDropDown/ConnectScannerButton';
import { ActionsDropDownHighlightedTrackers } from '../ActionsDropDownHighlightedTrackers/ActionsDropDownHighlightedTrackers';
import { CreateTrackerButton } from '../CreateTracker/CreateTrackerButton';
import { ShippingButton } from '../ShippingButton';
import { Tables } from '../Tables/Tables';
import { Column } from './Column';
import { SearchBarAndResultsCount } from './SearchBarAndResultsCount';
import { TopBar } from './TopBar';

interface Props {
  isLeftClosed: boolean;
  onLeftOpen: () => void;
}

export const TrackerTableColumn = React.memo(function TrackerTableColumn({
  isLeftClosed,
  onLeftOpen,
}: Props) {
  const searchValue = useStore((s) => s.trackers.searchValue);
  const { setSearchValue } = useStore((s) => s.trackers.actions);
  const highlightedTrackers = useHighlightedTrackers();
  const isAdmin = useIsAdmin();
  const isReseller = useIsReseller();

  const styleArrowLeft = useSpring({
    to: {
      opacity: isLeftClosed ? 1 : 0,
      display: isLeftClosed ? 'block' : 'none',
    },
    reverse: !isLeftClosed,
    delay: 100,
  });

  const buttonContainerLeftStyle = useSpring({
    to: {
      left: isLeftClosed ? 18 : 0,
    },
    config: {
      tension: 500,
      friction: 35,
    },
  });

  const topBarContent = useMemo(
    () => (
      <Bar>
        <div style={{ display: 'flex', position: 'relative' }}>
          <ArrowLeft onClick={onLeftOpen} style={styleArrowLeft} />
          <ButtonContainer style={buttonContainerLeftStyle}>
            <CreateTrackerButton />
          </ButtonContainer>
        </div>
        <SearchBarAndResultsCount searchValue={searchValue} setSearchValue={setSearchValue} />
        <div style={{ display: 'flex', position: 'relative' }}>
          <ButtonContainer style={{ right: 0 }}>
            {highlightedTrackers.length > 0 && <ActionsDropDownHighlightedTrackers />}
            {(isAdmin || isReseller) && <ShippingButton />}
            <ConnectScannerButton />
            <ActionsDropDown />
          </ButtonContainer>
        </div>
      </Bar>
    ),
    [onLeftOpen, setSearchValue, searchValue, styleArrowLeft, buttonContainerLeftStyle],
  );

  return (
    <Column topBarContent={topBarContent}>
      <Tables />
    </Column>
  );
});

const Bar = styled(TopBar)`
  justify-content: space-between;
`;

const ButtonContainer = styled(animated.div)`
  display: flex;
  align-items: center;
  height: 100%;
  position: absolute;

  div:not(:last-of-type) {
    margin-right: 3px;
  }
`;

const ArrowLeft = animated(IconArrowDoubleRight);
